import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useAuth } from "../hooks/useAuth"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {
  useSpecs,
  useSpecItems,
  useSpecTemplates,
  INITIAL_WRITE_NAME,
  type EquipmentSpec,
  type SpecItem,
  type SpecItemRevision,
  type SpecPart,
  type SpecTemplate,
  type TemplateItem,
} from "../hooks/useSpecs"
import {FileText, Plus, ArrowLeft, Building2, Hash, Clock, CheckCircle2, XCircle, AlertTriangle, History, Pencil, Trash2, Archive, ArchiveRestore, Search, Save, FolderDown, Sparkles, } from 'lucide-react'
import { SpecItemEditModal } from "./SpecItemEditModal"
import { SpecPdfExport } from "./SpecPdfExport"
import { ActualSpecPdfExport } from "./ActualSpecPdfExport"

// 2단계 구분(대분류/중분류) 그룹핑을 위한 타입
interface GroupedItems {
  category: string
  items: SpecItem[]
}

export function SpecManagement() {
  const auth = useAuth()
  const canApprove = auth.isManager || auth.isCLevel || auth.isAdmin // 팀장급 이상 승인 가능

  const canManage = auth.isManager || auth.isCLevel || auth.isAdmin // 팀장급 이상: 삭제/숨김/복원

  const { specs, loading, fetchSpecs, createSpec, updateSpec, deleteSpec, archiveSpec } = useSpecs()
  const {
    items,
    revisions,
    fetchItems,
    fetchRevisions,
    submitRevision,
    submitDeleteRevision,
    addCategoryItem,
    deleteItem,
    approveChangeset,
    rejectRevision,
    rejectChangeset,
    updateActualSpec,
    ensureMandatoryCategory,
    importItems,
  } = useSpecItems()

  const { templates, fetchTemplates, saveTemplate, deleteTemplate, parseTemplateItems } =
    useSpecTemplates()

  const [selectedSpec, setSelectedSpec] = useState<EquipmentSpec | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  // 사양서 이름/메타 정보 수정 모달
  const [showEditSpec, setShowEditSpec] = useState(false)
  const [savingSpec, setSavingSpec] = useState(false)
  // 목록 탭(활성/보관) + 검색
  const [listTab, setListTab] = useState<"active" | "archived">("active")
  const [search, setSearch] = useState("")
  const [tab, setTab] = useState<"items" | "actual" | "history" | "approved">("items")
  const [savingActual, setSavingActual] = useState<string | null>(null)
  const [editTarget, setEditTarget] = useState<{ mode: "create" | "update"; item?: SpecItem; defaultCategory?: string; categoryOnly?: boolean } | null>(null)
  // 템플릿 저장/불러오기 모달
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [showLoadTemplate, setShowLoadTemplate] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [importingTemplate, setImportingTemplate] = useState(false)
  // 방안 B: 개정묶음 일괄 승인을 위한 선택 상태
  const [selectedRevIds, setSelectedRevIds] = useState<string[]>([])
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [approving, setApproving] = useState(false)
  // 개정묶음 일괄 반려용 상태
  const [rejecting, setRejecting] = useState(false)

  useEffect(() => {
    fetchSpecs()
  }, [fetchSpecs])

  // 상세 진입 시 항목/이력 로드 + 필수 대분류(공장입력전원) 보장
  useEffect(() => {
    if (selectedSpec?._id) {
      ;(async () => {
        await fetchItems(selectedSpec._id)
        await ensureMandatoryCategory(selectedSpec, editorMeta)
      })()
      fetchRevisions(selectedSpec._id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpec?._id, fetchItems, fetchRevisions, ensureMandatoryCategory])

  // 상세 진입 시 history push -> 브라우저 뒤로가기/헤더 뒤로가기로 목록 복귀
  useEffect(() => {
    if (!selectedSpec) return
    window.history.pushState({ specDetail: true }, "")
    const onPop = () => setSelectedSpec(null)
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [selectedSpec?._id])

  // 사양서별 미승인 revision 개수 집계
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({})
  useEffect(() => {
    let cancelled = false
    async function loadPending() {
      try {
        const { list } = await lumi.entities.spec_item_revisions.list({
          filter: { approvalStatus: "pending" },
        })
        if (cancelled) return
        const counts: Record<string, number> = {}
        ;(list || []).forEach((r: any) => {
          counts[r.specId] = (counts[r.specId] || 0) + 1
        })
        setPendingCounts(counts)
      } catch (e) {
        console.error("미승인 집계 실패:", e)
      }
    }
    loadPending()
    return () => {
      cancelled = true
    }
  }, [specs, revisions])

  const pendingRevisions = useMemo(
    () => revisions.filter((r) => r.approvalStatus === "pending"),
    [revisions]
  )

  // 변경 이력에서 반려된 항목은 목록에서 제외 (미승인/승인만 표시)
  const visibleRevisions = useMemo(
    () => revisions.filter((r) => r.approvalStatus !== "rejected"),
    [revisions]
  )

  // 승인된 변경 이력만 (별도 탭)
  const approvedRevisions = useMemo(
    () => revisions.filter((r) => r.approvalStatus === "approved"),
    [revisions]
  )

  // 항목별 미승인 여부 맵
  const pendingByItem = useMemo(() => {
    const map: Record<string, SpecItemRevision> = {}
    pendingRevisions.forEach((r) => {
      if (r.itemId) map[r.itemId] = r
    })
    return map
  }, [pendingRevisions])

  // 목록 필터링: 활성/보관 탭 + 검색어
  const visibleSpecs = useMemo(() => {
    const q = search.trim().toLowerCase()
    return specs.filter((s) => {
      const isArchived = !!s.archived
      if (listTab === "active" && isArchived) return false
      if (listTab === "archived" && !isArchived) return false
      if (!q) return true
      return (
        (s.specTitle || "").toLowerCase().includes(q) ||
        (s.sourceCompany || "").toLowerCase().includes(q) ||
        (s.docNumber || "").toLowerCase().includes(q)
      )
    })
  }, [specs, listTab, search])

  const archivedCount = useMemo(() => specs.filter((s) => s.archived).length, [specs])
  const activeCount = useMemo(() => specs.filter((s) => !s.archived).length, [specs])

  // 대분류 그룹핑
  const grouped: GroupedItems[] = useMemo(() => {
    const order: string[] = []
    const bucket: Record<string, SpecItem[]> = {}
    items.forEach((it) => {
      if (!bucket[it.category]) {
        bucket[it.category] = []
        order.push(it.category)
      }
      bucket[it.category].push(it)
    })
    return order.map((c) => ({ category: c, items: bucket[c] }))
  }, [items])

  // 템플릿 불러오기 초기 1회 제한:
  // 필수 대분류(공장입력전원)는 기본 자동 생성되므로 제외하고,
  // 그 외 실제 입력된 항목이 하나라도 있으면 템플릿 불러오기를 막는다.
  const MANDATORY_CATEGORY = "공장입력전원"
  const hasUserItems = useMemo(
    () => items.some((it) => it.category !== MANDATORY_CATEGORY),
    [items]
  )

  const editorMeta = { userId: auth.user?.userId || "", userName: auth.userName || auth.user?.email || "" }

  // 마지막 수정일(최근 사양 승인일) 표기용 포맷
  const fmtDate = (s?: string) =>
    s
      ? new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
      : "-"

  // 수정 요청 저장(미승인 상태)
  const handleSubmitRevision = useCallback(
    async (payload: {
      changeType: "create" | "update"
      itemId?: string
      category: string
      subCategory: string
      requirement: string
      guideline: string
      remark: string
      parts: SpecPart[]
      changeReason: string
      requestedByName: string
      before?: SpecItem
    }) => {
      if (!selectedSpec) return
      try {
        await submitRevision(
          selectedSpec._id,
          {
            changeType: payload.changeType,
            itemId: payload.itemId,
            category: payload.category,
            subCategory: payload.subCategory,
            requirementBefore: payload.before?.requirement || "",
            requirementAfter: payload.requirement,
            guidelineBefore: payload.before?.guideline || "",
            guidelineAfter: payload.guideline,
            remarkBefore: payload.before?.remark || "",
            remarkAfter: payload.remark,
            partsBefore: JSON.stringify(payload.before?.parts || []),
            partsAfter: JSON.stringify(payload.parts || []),
            changeReason: payload.changeReason,
            requestedByName: payload.requestedByName,
          },
          editorMeta
        )
        toast.success("수정 요청이 등록되었습니다. 팀장 승인 후 반영됩니다.")
        setEditTarget(null)
      } catch (e) {
        console.error(e)
        toast.error("수정 요청 등록에 실패했습니다.")
      }
    },
    [selectedSpec, submitRevision, editorMeta]
  )

  // 대분류 항목 즉시 추가 (팀장 승인 불필요) - categoryOnly 모드용
  const handleAddCategory = useCallback(
    async (payload: { category: string; subCategory: string; remark: string }) => {
      if (!selectedSpec) return
      if (!payload.category.trim()) {
        toast.error("대분류를 입력하세요.")
        return
      }
      try {
        await addCategoryItem(
          selectedSpec,
          { category: payload.category.trim(), subCategory: payload.subCategory, remark: payload.remark },
          editorMeta
        )
        toast.success("대분류 항목이 추가되었습니다. (승인 불필요)")
        setEditTarget(null)
      } catch (e) {
        console.error(e)
        toast.error("대분류 항목 추가에 실패했습니다.")
      }
    },
    [selectedSpec, addCategoryItem, editorMeta]
  )

  // 개별 revision 선택 토글
  const toggleRevSelect = useCallback((id: string) => {
    setSelectedRevIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }, [])

  // 미승인 전체 선택/해제
  const toggleSelectAllPending = useCallback(() => {
    const pendingIds = pendingRevisions.map((r) => r._id)
    setSelectedRevIds((prev) =>
      prev.length === pendingIds.length ? [] : pendingIds
    )
  }, [pendingRevisions])

  // 방안 B: 선택된 pending revision들을 하나의 개정으로 일괄 승인 (버전 +1)
  const handleApproveChangeset = async (changesetName: string) => {
    if (!selectedSpec) return
    const targets = pendingRevisions.filter((r) => selectedRevIds.includes(r._id))
    if (targets.length === 0) {
      toast.error("승인할 항목을 선택하세요.")
      return
    }
    setApproving(true)
    try {
      const result = await approveChangeset(selectedSpec, targets, editorMeta, changesetName)
      await fetchSpecs()
      setSelectedSpec((prev) =>
        prev
          ? {
              ...prev,
              currentVersion: result?.version ?? (prev.currentVersion || 1) + 1,
              lastApprovedAt: result?.approvedAt ?? new Date().toISOString(),
              lastApprovedVersion: result?.version,
              lastChangesetName: result?.changesetName ?? changesetName,
              totalRevisionCount:
                result?.totalRevisionCount ?? (prev.totalRevisionCount || 0) + targets.length,
            }
          : prev
      )
      setSelectedRevIds([])
      setShowApproveModal(false)
      toast.success(`${targets.length}건이 v${result?.version} 개정으로 일괄 승인되었습니다.`)
    } catch (e) {
      console.error(e)
      toast.error("일괄 승인 처리에 실패했습니다.")
    } finally {
      setApproving(false)
    }
  }

  const handleReject = async (rev: SpecItemRevision) => {
    if (!selectedSpec) return
    if (!window.confirm("이 수정 요청을 반려하시겠습니까? 반려된 항목은 DB에서 완전히 삭제됩니다.")) return
    try {
      await rejectRevision(selectedSpec._id, rev._id)
      toast.success("반려 처리되었습니다. (완전 삭제)")
    } catch (e) {
      console.error(e)
      toast.error("반려 처리에 실패했습니다.")
    }
  }

  // 선택된 pending revision들을 한 번에 일괄 반려 (DB에서 완전 삭제)
  const handleRejectChangeset = async () => {
    if (!selectedSpec) return
    const targets = pendingRevisions.filter((r) => selectedRevIds.includes(r._id))
    if (targets.length === 0) {
      toast.error("반려할 항목을 선택하세요.")
      return
    }
    if (!window.confirm(`선택한 ${targets.length}건을 일괄 반려하시겠습니까? 반려된 항목은 DB에서 완전히 삭제됩니다.`)) return
    setRejecting(true)
    try {
      await rejectChangeset(selectedSpec._id, targets)
      setSelectedRevIds([])
      toast.success(`${targets.length}건이 일괄 반려되었습니다. (완전 삭제)`)
    } catch (e) {
      console.error(e)
      toast.error("일괄 반려 처리에 실패했습니다.")
    } finally {
      setRejecting(false)
    }
  }

  // 사양 항목 삭제 요청(delete revision 생성) - 팀장 승인 시 DB에서 완전 삭제
  const handleRequestDeleteItem = useCallback(
    async (item: SpecItem, requestedByName: string, changeReason: string) => {
      if (!selectedSpec) return
      try {
        await submitDeleteRevision(selectedSpec._id, item, editorMeta, requestedByName, changeReason)
        toast.success("삭제 요청이 등록되었습니다. 팀장 승인 후 완전 삭제됩니다.")
        setEditTarget(null)
      } catch (e) {
        console.error(e)
        toast.error("삭제 요청 등록에 실패했습니다.")
      }
    },
    [selectedSpec, submitDeleteRevision, editorMeta]
  )

  // 사양 항목 탭 <관리>의 삭제 버튼 처리
  // - 팀장급 이상(canManage): 승인 없이 즉시 DB에서 완전 삭제
  // - 팀원: 삭제 요청(delete revision) 등록 -> 팀장 승인 시 완전 삭제
  const handleDeleteItemFromTable = useCallback(
    async (item: SpecItem) => {
      if (!selectedSpec) return
      if (canManage) {
        if (!window.confirm(`"${item.subCategory}" 항목을 삭제하시겠습니까? 승인 없이 즉시 완전 삭제됩니다.`)) return
        try {
          await deleteItem(selectedSpec._id, item._id)
          toast.success("항목이 삭제되었습니다. (즉시 완전 삭제)")
        } catch (e) {
          console.error(e)
          toast.error("삭제에 실패했습니다.")
        }
      } else {
        if (!window.confirm(`"${item.subCategory}" 항목 삭제를 요청하시겠습니까? 팀장 승인 후 완전 삭제됩니다.`)) return
        try {
          await submitDeleteRevision(
            selectedSpec._id,
            item,
            editorMeta,
            auth.userName || "",
            "항목 삭제 요청"
          )
          toast.success("삭제 요청이 등록되었습니다. 팀장 승인 후 완전 삭제됩니다.")
        } catch (e) {
          console.error(e)
          toast.error("삭제 요청 등록에 실패했습니다.")
        }
      }
    },
    [selectedSpec, canManage, deleteItem, submitDeleteRevision, editorMeta, auth.userName]
  )

  // 실제 제작 사양 저장 (승인 불필요, 모든 사용자 가능)
  const handleSaveActual = useCallback(
    async (itemId: string, actualSpec: string) => {
      if (!selectedSpec) return
      setSavingActual(itemId)
      try {
        await updateActualSpec(selectedSpec._id, itemId, actualSpec)
        toast.success("실제 제작 사양이 저장되었습니다.")
      } catch (e) {
        console.error(e)
        toast.error("저장에 실패했습니다.")
      } finally {
        setSavingActual(null)
      }
    },
    [selectedSpec, updateActualSpec]
  )

  // 현재 사양 항목들을 템플릿으로 저장
  const handleSaveTemplate = async (templateName: string, description: string) => {
    if (!templateName.trim()) {
      toast.error("템플릿 이름을 입력하세요.")
      return
    }
    const templateItems: TemplateItem[] = items.map((it) => ({
      category: it.category,
      subCategory: it.subCategory,
      requirement: it.requirement || "",
      guideline: it.guideline || "",
      remark: it.remark || "",
      unit: it.unit || "",
      parts: Array.isArray(it.parts) ? it.parts : [],
    }))
    if (templateItems.length === 0) {
      toast.error("저장할 사양 항목이 없습니다.")
      return
    }
    setSavingTemplate(true)
    try {
      await saveTemplate(templateName.trim(), templateItems, editorMeta, description.trim())
      toast.success(`"${templateName.trim()}" 템플릿으로 저장되었습니다.`)
      setShowSaveTemplate(false)
    } catch (e) {
      console.error(e)
      toast.error("템플릿 저장에 실패했습니다.")
    } finally {
      setSavingTemplate(false)
    }
  }

  // 템플릿 불러오기: 선택한 템플릿 항목을 현재 사양서에 즉시 추가
  const handleLoadTemplate = async (tpl: SpecTemplate) => {
    if (!selectedSpec) return
    if (hasUserItems) {
      toast.error("이미 입력된 사양 항목이 있어 템플릿을 불러올 수 없습니다.")
      return
    }
    const tplItems = parseTemplateItems(tpl)
    if (tplItems.length === 0) {
      toast.error("이 템플릿에는 항목이 없습니다.")
      return
    }
    if (!window.confirm(`"${tpl.templateName}" 템플릿의 ${tplItems.length}개 항목을 현재 사양서에 추가하시겠습니까?`)) return
    setImportingTemplate(true)
    try {
      const count = await importItems(selectedSpec._id, tplItems, editorMeta)
      toast.success(`${count}개 항목을 불러왔습니다.`)
      setShowLoadTemplate(false)
    } catch (e) {
      console.error(e)
      toast.error("템플릿 불러오기에 실패했습니다.")
    } finally {
      setImportingTemplate(false)
    }
  }

  const handleDeleteTemplate = async (tpl: SpecTemplate) => {
    if (!window.confirm(`"${tpl.templateName}" 템플릿을 삭제하시겠습니까?`)) return
    try {
      await deleteTemplate(tpl._id)
      toast.success("템플릿이 삭제되었습니다.")
    } catch (e) {
      console.error(e)
      toast.error("템플릿 삭제에 실패했습니다.")
    }
  }

  // 사양서 이름/원청사/문서번호 수정 저장
  const handleUpdateSpecMeta = async (data: {
    specTitle: string
    sourceCompany: string
    docNumber: string
  }) => {
    if (!selectedSpec) return
    if (!data.specTitle.trim()) {
      toast.error("사양서 제목을 입력하세요.")
      return
    }
    setSavingSpec(true)
    try {
      await updateSpec(selectedSpec._id, {
        specTitle: data.specTitle.trim(),
        sourceCompany: data.sourceCompany.trim(),
        docNumber: data.docNumber.trim(),
      })
      setSelectedSpec((prev) =>
        prev
          ? {
              ...prev,
              specTitle: data.specTitle.trim(),
              sourceCompany: data.sourceCompany.trim(),
              docNumber: data.docNumber.trim(),
            }
          : prev
      )
      setShowEditSpec(false)
      toast.success("사양서 정보가 수정되었습니다.")
    } catch (e) {
      console.error(e)
      toast.error("사양서 정보 수정에 실패했습니다.")
    } finally {
      setSavingSpec(false)
    }
  }

  // 보관/복원 처리 (팀장급)
  const handleArchive = async (spec: EquipmentSpec, archived: boolean) => {
    const msg = archived ? "이 사양서를 보관(숨김) 처리하시겠습니까?" : "이 사양서를 활성 목록으로 복원하시겠습니까?"
    if (!window.confirm(msg)) return
    try {
      await archiveSpec(spec._id, archived)
      toast.success(archived ? "보관 처리되었습니다." : "복원되었습니다.")
    } catch (e) {
      console.error(e)
      toast.error("처리에 실패했습니다.")
    }
  }

  // 목록에서 삭제 (팀장급)
  const handleDeleteFromList = async (spec: EquipmentSpec) => {
    if (!window.confirm("이 사양서를 완전히 삭제하시겠습니까? 되돌릴 수 없습니다.")) return
    try {
      await deleteSpec(spec._id)
      toast.success("삭제되었습니다.")
    } catch (e) {
      console.error(e)
      toast.error("삭제에 실패했습니다.")
    }
  }

  // ==================== 목록 화면 ====================
  if (!selectedSpec) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">제작 사양서</h1>
              <p className="text-sm text-gray-500">요구사항 변경 이력을 추적하고 최종 사양서를 관리합니다.</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">사양서 생성</span>
          </button>
        </div>

        {/* 탭 + 검색 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-1 border-b border-gray-200">
            <button
              onClick={() => setListTab("active")}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                listTab === "active" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              활성 ({activeCount})
            </button>
            <button
              onClick={() => setListTab("archived")}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                listTab === "archived" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Archive className="w-4 h-4" />
              보관됨 ({archivedCount})
            </button>
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="제목/원청사/문서번호 검색"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">불러오는 중...</div>
        ) : visibleSpecs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {search.trim()
                ? "검색 결과가 없습니다."
                : listTab === "archived"
                ? "보관된 사양서가 없습니다."
                : "등록된 제작 사양서가 없습니다."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleSpecs.map((spec) => (
              <div
                key={spec._id}
                className={`relative text-left bg-white rounded-xl border p-5 hover:shadow-md transition-all ${
                  spec.archived ? "border-gray-200 opacity-80" : "border-gray-200 hover:border-orange-300"
                }`}
              >
                {/* 팀장급 액션 (보관/복원/삭제) */}
                {canManage && (
                  <div className="absolute top-3 right-3 flex items-center gap-1">
                    {spec.archived ? (
                      <button
                        onClick={() => handleArchive(spec, false)}
                        className="p-1.5 rounded-lg hover:bg-green-50 text-green-600"
                        title="복원"
                      >
                        <ArchiveRestore className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchive(spec, true)}
                        className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"
                        title="보관(숨김)"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteFromList(spec)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                      title="삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <button onClick={() => setSelectedSpec(spec)} className="block w-full text-left">
                  <div className="flex items-start justify-between gap-2 mb-3 pr-16">
                    <h3 className="font-bold text-gray-900 line-clamp-2">{spec.specTitle}</h3>
                    {pendingCounts[spec._id] > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3" />
                        미승인 {pendingCounts[spec._id]}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5 text-sm text-gray-600">
                    {spec.sourceCompany && (
                      <p className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-gray-400" />
                        {spec.sourceCompany}
                      </p>
                    )}
                    {spec.docNumber && (
                      <p className="flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5 text-gray-400" />
                        {spec.docNumber}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        spec.status === "confirmed"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {spec.status === "confirmed" ? "확정" : "작성중"}
                    </span>
                    <span className="text-xs font-semibold text-orange-600">
                      {spec.lastChangesetName ? spec.lastChangesetName : "미개정"}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
                    <span className="font-semibold text-gray-500">
                      총 수정 반영 횟수: {spec.totalRevisionCount || 0}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      마지막 수정일: {spec.lastApprovedAt ? fmtDate(spec.lastApprovedAt) : "승인 이력 없음"}
                    </span>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}

        {showCreate && (
          <CreateSpecModal
            onClose={() => setShowCreate(false)}
            onCreate={async (data) => {
              try {
                await createSpec(data, editorMeta)
                toast.success("사양서가 생성되었습니다.")
                setShowCreate(false)
              } catch (e) {
                console.error(e)
                toast.error("사양서 생성에 실패했습니다.")
              }
            }}
          />
        )}
      </div>
    )
  }

  // ==================== 상세 화면 ====================
  return (
    <div className="p-4 sm:p-6">
      {/* 헤더 */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          <button
            onClick={() => setSelectedSpec(null)}
            className="shrink-0 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{selectedSpec.specTitle}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
              {selectedSpec.sourceCompany && <span>{selectedSpec.sourceCompany}</span>}
              {selectedSpec.docNumber && <span>{selectedSpec.docNumber}</span>}
              <span className="font-semibold text-orange-600">
                현재 버전{selectedSpec.lastChangesetName ? ` : ${selectedSpec.lastChangesetName}` : ""}
              </span>
              <span className="font-semibold text-gray-700">
                총 수정 반영 횟수: {selectedSpec.totalRevisionCount || 0}
              </span>
              <span className="inline-flex items-center gap-1 text-gray-400">
                <Clock className="w-3.5 h-3.5" />
                마지막 수정일: {selectedSpec.lastApprovedAt ? fmtDate(selectedSpec.lastApprovedAt) : "승인 이력 없음"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowEditSpec(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium"
            title="사양서 이름/원청사/문서번호 수정"
          >
            <Pencil className="w-4 h-4" />
            <span className="hidden sm:inline">정보 수정</span>
          </button>
          <SpecPdfExport spec={selectedSpec} grouped={grouped} />
          <button
            onClick={() => setShowSaveTemplate(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium"
            title="현재 사양 항목을 템플릿으로 저장"
          >
            <Save className="w-4 h-4" />
            <span className="hidden sm:inline">템플릿 저장</span>
          </button>
          <button
            onClick={() => {
              if (hasUserItems) {
                toast.error("이미 입력된 사양 항목이 있어 템플릿을 불러올 수 없습니다. 템플릿 불러오기는 항목이 비어있는 초기 상태에서만 가능합니다.")
                return
              }
              fetchTemplates()
              setShowLoadTemplate(true)
            }}
            disabled={hasUserItems}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title={hasUserItems ? "이미 입력된 항목이 있어 템플릿을 불러올 수 없습니다" : "저장된 템플릿에서 항목 불러오기"}
          >
            <FolderDown className="w-4 h-4" />
            <span className="hidden sm:inline">불러오기</span>
          </button>
          <button
            onClick={() => setEditTarget({ mode: "create", categoryOnly: true })}
            className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            대분류 항목 추가
          </button>
          {auth.isAdmin && (
            <button
              onClick={async () => {
                if (!window.confirm("이 사양서를 삭제하시겠습니까?")) return
                await deleteSpec(selectedSpec._id)
                toast.success("삭제되었습니다.")
                setSelectedSpec(null)
              }}
              className="p-2 rounded-lg hover:bg-red-50 text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* 탭 */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => setTab("items")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "items" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          사양 항목
        </button>
        <button
          onClick={() => setTab("actual")}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "actual" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Pencil className="w-4 h-4" />
          실제작사양
        </button>
        <button
          onClick={() => setTab("history")}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "history" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <History className="w-4 h-4" />
          변경 이력
          {pendingRevisions.length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
              미승인 {pendingRevisions.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("approved")}
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "approved" ? "border-orange-600 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          승인된 변경이력
          {approvedRevisions.length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              {approvedRevisions.length}
            </span>
          )}
        </button>
      </div>

      {tab === "items" ? (
        <SpecItemsTable
          grouped={grouped}
          pendingByItem={pendingByItem}
          canManage={canManage}
          currentVersion={selectedSpec.currentVersion || 1}
          onEdit={(item) => setEditTarget({ mode: "update", item })}
          onAddInCategory={(category) => setEditTarget({ mode: "create", defaultCategory: category })}
          onDelete={handleDeleteItemFromTable}
        />
      ) : tab === "actual" ? (
        <ActualSpecTab
          spec={selectedSpec}
          grouped={grouped}
          savingId={savingActual}
          onSave={handleSaveActual}
        />
      ) : tab === "approved" ? (
        <RevisionHistory
          revisions={approvedRevisions}
          canApprove={false}
          readOnly
          selectedRevIds={selectedRevIds}
          onToggleSelect={toggleRevSelect}
          onToggleSelectAll={toggleSelectAllPending}
          pendingCount={0}
          onOpenApprove={() => {}}
          onReject={handleReject}
          onRejectSelected={handleRejectChangeset}
          rejecting={rejecting}
          emptyText="승인된 변경 이력이 없습니다."
        />
      ) : (
        <RevisionHistory
          revisions={pendingRevisions}
          canApprove={canApprove}
          selectedRevIds={selectedRevIds}
          onToggleSelect={toggleRevSelect}
          onToggleSelectAll={toggleSelectAllPending}
          pendingCount={pendingRevisions.length}
          onOpenApprove={() => {
            if (selectedRevIds.length === 0) {
              toast.error("개정으로 묶을 항목을 먼저 선택하세요.")
              return
            }
            setShowApproveModal(true)
          }}
          onReject={handleReject}
          onRejectSelected={handleRejectChangeset}
          rejecting={rejecting}
          emptyText="대기 중인 미승인 변경 요청이 없습니다."
        />
      )}

      {showApproveModal && (
        <ApproveChangesetModal
          count={selectedRevIds.length}
          nextVersion={(selectedSpec.currentVersion || 1) + 1}
          approving={approving}
          onClose={() => setShowApproveModal(false)}
          onConfirm={handleApproveChangeset}
        />
      )}

      {showEditSpec && (
        <EditSpecModal
          spec={selectedSpec}
          saving={savingSpec}
          onClose={() => setShowEditSpec(false)}
          onSave={handleUpdateSpecMeta}
        />
      )}

      {showSaveTemplate && (
        <SaveTemplateModal
          itemCount={items.length}
          saving={savingTemplate}
          onClose={() => setShowSaveTemplate(false)}
          onSave={handleSaveTemplate}
        />
      )}

      {showLoadTemplate && (
        <LoadTemplateModal
          templates={templates}
          importing={importingTemplate}
          parseTemplateItems={parseTemplateItems}
          onClose={() => setShowLoadTemplate(false)}
          onLoad={handleLoadTemplate}
          onDelete={handleDeleteTemplate}
        />
      )}

      {editTarget && (
        <SpecItemEditModal
          mode={editTarget.mode}
          item={editTarget.item}
          defaultCategory={editTarget.defaultCategory}
          categoryOnly={editTarget.categoryOnly}
          defaultRequestedBy={auth.userName || ""}
          canDelete={canManage && editTarget.mode === "update" && !!editTarget.item}
          onRequestDelete={(requestedByName, changeReason) => {
            if (editTarget.item) handleRequestDeleteItem(editTarget.item, requestedByName, changeReason)
          }}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => {
            // 대분류만 추가하는 경우에는 팀장 승인 없이 즉시 반영
            if (editTarget.categoryOnly) {
              handleAddCategory({
                category: data.category,
                subCategory: data.subCategory,
                remark: data.remark,
              })
              return
            }
            handleSubmitRevision({
              changeType: editTarget.mode,
              itemId: editTarget.item?._id,
              before: editTarget.item,
              ...data,
            })
          }}
        />
      )}
    </div>
  )
}

// 대분류 섹션별 색상 팔레트 (순환 배정) - 좌측 컬러바/뱃지에 사용
const CATEGORY_COLORS = [
  { bar: "bg-orange-500", badge: "bg-orange-100 text-orange-700", header: "bg-orange-50/70", iconText: "text-orange-600" },
  { bar: "bg-sky-500", badge: "bg-sky-100 text-sky-700", header: "bg-sky-50/70", iconText: "text-sky-600" },
  { bar: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700", header: "bg-emerald-50/70", iconText: "text-emerald-600" },
  { bar: "bg-violet-500", badge: "bg-violet-100 text-violet-700", header: "bg-violet-50/70", iconText: "text-violet-600" },
  { bar: "bg-rose-500", badge: "bg-rose-100 text-rose-700", header: "bg-rose-50/70", iconText: "text-rose-600" },
  { bar: "bg-amber-500", badge: "bg-amber-100 text-amber-700", header: "bg-amber-50/70", iconText: "text-amber-600" },
  { bar: "bg-teal-500", badge: "bg-teal-100 text-teal-700", header: "bg-teal-50/70", iconText: "text-teal-600" },
  { bar: "bg-indigo-500", badge: "bg-indigo-100 text-indigo-700", header: "bg-indigo-50/70", iconText: "text-indigo-600" },
]
const categoryColor = (idx: number) => CATEGORY_COLORS[idx % CATEGORY_COLORS.length]

// ==================== 사양 항목 표 (대분류별 섹션 카드 + 미니 테이블) ====================
function SpecItemsTable({
  grouped,
  pendingByItem,
  canManage,
  currentVersion,
  onEdit,
  onAddInCategory,
  onDelete,
}: {
  grouped: GroupedItems[]
  pendingByItem: Record<string, SpecItemRevision>
  canManage: boolean
  currentVersion: number
  onEdit: (item: SpecItem) => void
  onAddInCategory: (category: string) => void
  onDelete: (item: SpecItem) => void
}) {
  if (grouped.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">등록된 사양 항목이 없습니다. "항목 추가"로 시작하세요.</p>
      </div>
    )
  }
  // 최신 개정 항목이 하나라도 있으면 상단에 범례를 노출
  const hasLatest =
    currentVersion > 1 &&
    grouped.some((g) => g.items.some((it) => (it.version ?? 0) === currentVersion))
  return (
    <div className="space-y-5">
      {hasLatest && (
        <div className="flex flex-wrap items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-700">
            <Sparkles className="w-3 h-3" />
            최신 개정 (v{currentVersion})
          </span>
          <span className="text-sm text-emerald-800">
            초록색으로 표시된 항목은 <b>가장 최근 개정(v{currentVersion})에서 새로 반영·수정된 항목</b>입니다.
          </span>
        </div>
      )}
      {grouped.map((group, gIdx) => {
        const color = categoryColor(gIdx)
        const pendingInGroup = group.items.filter((it) => pendingByItem[it._id]).length
        return (
          <section
            key={group.category}
            className="relative bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
          >
            {/* 좌측 컬러바 */}
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${color.bar}`} />
            {/* 섹션 헤더 */}
            <div className={`flex items-center justify-between gap-3 pl-5 pr-4 py-3 border-b border-gray-200 ${color.header}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className={`w-4 h-4 shrink-0 ${color.iconText}`} />
                <h3 className="font-bold text-gray-900 truncate">{group.category}</h3>
                <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${color.badge}`}>
                  {group.items.length}개 항목
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {pendingInGroup > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    <AlertTriangle className="w-3 h-3" />
                    미승인 {pendingInGroup}
                  </span>
                )}
                <button
                  onClick={() => onAddInCategory(group.category)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white border border-orange-300 hover:bg-orange-50 text-orange-700 rounded-lg text-xs font-semibold shadow-sm"
                  title={`"${group.category}" 대분류에 항목 추가`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  추가
                </button>
              </div>
            </div>
            {/* 미니 테이블 (대분류 컬럼 제거) */}
            <div className="overflow-x-auto pl-1.5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-semibold w-40">중분류</th>
                    <th className="px-4 py-2.5 font-semibold">요구 사양</th>
                    <th className="px-4 py-2.5 font-semibold w-96">비고</th>
                    <th className="px-4 py-2.5 font-semibold w-24 text-center">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const pending = pendingByItem[item._id]
                    // 최신 개정 하이라이트: 현재 버전과 항목 버전이 같고, 최초 생성(v1)이 아닌 경우
                    // = 가장 최근 개정에서 새로 반영(신규/수정)된 항목
                    const isLatest = currentVersion > 1 && (item.version ?? 0) === currentVersion
                    return (
                      <tr
                        key={item._id}
                        className={`border-b border-gray-100 last:border-0 align-top transition-colors ${
                          isLatest ? "bg-emerald-50/70 hover:bg-emerald-50" : "hover:bg-orange-50/30"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-gray-800 border-r border-gray-100">
                          <div className="flex items-center gap-1.5">
                            {isLatest && (
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title="최신 개정 항목" />
                            )}
                            {item.subCategory}
                          </div>
                          {isLatest && (
                            <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                              <Sparkles className="w-3 h-3" />
                              최신 개정 (v{item.version})
                            </span>
                          )}
                          {pending && (
                            <span className="mt-1 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700">
                              <AlertTriangle className="w-3 h-3" />
                              미승인
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 border-r border-gray-100">
                          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: item.requirement || "-" }} />
                        </td>
                        <td className="px-4 py-3 text-gray-600 border-r border-gray-100">
                          {item.remark ? (
                            <span className="whitespace-pre-line text-xs leading-relaxed">{item.remark}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => onEdit(item)}
                              className="p-1.5 rounded-lg hover:bg-orange-100 text-orange-600"
                              title="수정 요청"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onDelete(item)}
                              className="p-1.5 rounded-lg hover:bg-red-100 text-red-600"
                              title={canManage ? "삭제 (승인 없이 즉시 삭제)" : "삭제 요청 (팀장 승인 필요)"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ==================== 사용 부품 요약 셀 ====================
function PartsCell({ parts }: { parts?: SpecPart[] }) {
  const list = Array.isArray(parts) ? parts.filter((p) => (p.partName || p.partNumber || "").trim()) : []
  if (list.length === 0) return <span className="text-gray-300">-</span>
  return (
    <ul className="space-y-0.5">
      {list.map((p, i) => (
        <li key={i} className="text-xs leading-tight">
          <span className="font-medium text-gray-700">{p.partName || p.partNumber || "-"}</span>
          {p.partNumber && p.partName && <span className="text-gray-400"> ({p.partNumber})</span>}
          <span className="ml-1 text-orange-600 font-semibold">×{p.quantity ?? 1}</span>
          {p.maker && <span className="ml-1 text-gray-400">/ {p.maker}</span>}
        </li>
      ))}
    </ul>
  )
}

// ==================== 실제작사양 탭 (승인 불필요, 모든 사용자 직접 편집) ====================
function ActualSpecTab({
  spec,
  grouped,
  savingId,
  onSave,
}: {
  spec: EquipmentSpec
  grouped: GroupedItems[]
  savingId: string | null
  onSave: (itemId: string, actualSpec: string) => void
}) {
  // 항목별 로컬 편집 값 (초기값은 저장된 actualSpec)
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  // 항목 목록이 바뀌면 저장된 값으로 draft 초기화
  useEffect(() => {
    const next: Record<string, string> = {}
    grouped.forEach((g) => g.items.forEach((it) => (next[it._id] = it.actualSpec || "")))
    setDrafts(next)
  }, [grouped])

  const setDraft = (id: string, v: string) => setDrafts((prev) => ({ ...prev, [id]: v }))

  if (grouped.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">등록된 사양 항목이 없습니다. "사양 항목" 탭에서 먼저 항목을 추가하세요.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 안내 + 실제사양 전용 PDF 출력 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
        <p className="text-sm text-emerald-800">
          실제 제작 사양은 <b>승인 절차 없이</b> 누구나 바로 입력·수정할 수 있습니다. (개발 사양과 별개)
        </p>
        <ActualSpecPdfExport spec={spec} grouped={grouped} />
      </div>

      {grouped.map((group, gIdx) => {
        const color = categoryColor(gIdx)
        return (
          <section
            key={group.category}
            className="relative bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
          >
            {/* 좌측 컬러바 */}
            <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${color.bar}`} />
            {/* 섹션 헤더 */}
            <div className={`flex items-center gap-2 pl-5 pr-4 py-3 border-b border-gray-200 ${color.header}`}>
              <FileText className={`w-4 h-4 shrink-0 ${color.iconText}`} />
              <h3 className="font-bold text-gray-900 truncate">{group.category}</h3>
              <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${color.badge}`}>
                {group.items.length}개 항목
              </span>
            </div>
            {/* 미니 테이블 (대분류 컬럼 제거) */}
            <div className="overflow-x-auto pl-1.5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-semibold w-32">중분류</th>
                    <th className="px-4 py-2.5 font-semibold">요구 사양(참고)</th>
                    <th className="px-4 py-2.5 font-semibold">제작 가이드라인</th>
                    <th className="px-4 py-2.5 font-semibold w-48">사용 부품</th>
                    <th className="px-4 py-2.5 font-semibold">실제 제작 사양</th>
                    <th className="px-4 py-2.5 font-semibold w-24 text-center">저장</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const draft = drafts[item._id] ?? ""
                    const dirty = draft !== (item.actualSpec || "")
                    const saving = savingId === item._id
                    return (
                      <tr key={item._id} className="border-b border-gray-100 last:border-0 align-top">
                        <td className="px-4 py-3 font-medium text-gray-800 border-r border-gray-100 align-top">
                          {item.subCategory}
                        </td>
                        <td className="px-4 py-3 text-gray-500 border-r border-gray-100 align-top">
                          <div
                            className="prose prose-sm max-w-none text-gray-500"
                            dangerouslySetInnerHTML={{ __html: item.requirement || "-" }}
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-500 border-r border-gray-100 align-top">
                          <div
                            className="prose prose-sm max-w-none text-gray-500"
                            dangerouslySetInnerHTML={{ __html: item.guideline || "-" }}
                          />
                        </td>
                        <td className="px-4 py-3 text-gray-600 border-r border-gray-100 align-top">
                          <PartsCell parts={item.parts} />
                        </td>
                        <td className="px-4 py-3 border-r border-gray-100 align-top">
                          <textarea
                            value={draft}
                            onChange={(e) => setDraft(item._id, e.target.value)}
                            rows={3}
                            placeholder="실제 제작된 사양을 입력하세요."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-y"
                          />
                        </td>
                        <td className="px-4 py-3 text-center align-top">
                          <button
                            onClick={() => onSave(item._id, draft)}
                            disabled={!dirty || saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold"
                          >
                            {saving ? "저장 중" : dirty ? "저장" : "저장됨"}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ==================== 변경 이력 타임라인 (방안 B: 개정묶음 일괄 승인) ====================
function RevisionHistory({
  revisions,
  canApprove,
  selectedRevIds,
  onToggleSelect,
  onToggleSelectAll,
  pendingCount,
  onOpenApprove,
  onReject,
  onRejectSelected,
  rejecting,
  readOnly,
  emptyText,
}: {
  revisions: SpecItemRevision[]
  canApprove: boolean
  selectedRevIds: string[]
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  pendingCount: number
  onOpenApprove: () => void
  onReject: (rev: SpecItemRevision) => void
  onRejectSelected?: () => void
  rejecting?: boolean
  readOnly?: boolean
  emptyText?: string
}) {
  if (revisions.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">{emptyText || "변경 이력이 없습니다."}</p>
      </div>
    )
  }
  const fmt = (s?: string) => (s ? new Date(s).toLocaleString("ko-KR", { hour12: false }) : "-")
  const allSelected = pendingCount > 0 && selectedRevIds.length === pendingCount

  // 부품 JSON 문자열 파싱 (안전)
  const parseParts = (s?: string): SpecPart[] => {
    if (!s) return []
    try {
      const arr = JSON.parse(s)
      return Array.isArray(arr) ? arr : []
    } catch {
      return []
    }
  }

  const PartsList = ({ parts }: { parts: SpecPart[] }) =>
    parts.length > 0 ? (
      <ul className="space-y-0.5">
        {parts.map((p, i) => (
          <li key={i} className="text-xs leading-tight">
            <span className="font-medium text-gray-700">{p.partName || p.partNumber || "-"}</span>
            {p.partNumber && p.partName && <span className="text-gray-400"> ({p.partNumber})</span>}
            <span className="ml-1 text-orange-600 font-semibold">×{p.quantity ?? 1}</span>
            {p.maker && <span className="ml-1 text-gray-400">/ {p.maker}</span>}
          </li>
        ))}
      </ul>
    ) : (
      <span className="text-xs text-gray-300">-</span>
    )
  const statusMeta = (s: string) =>
    s === "pending"
      ? { label: "미승인", cls: "bg-amber-100 text-amber-700", icon: <Clock className="w-3.5 h-3.5" /> }
      : s === "approved"
      ? { label: "승인됨", cls: "bg-green-100 text-green-700", icon: <CheckCircle2 className="w-3.5 h-3.5" /> }
      : { label: "반려됨", cls: "bg-red-100 text-red-700", icon: <XCircle className="w-3.5 h-3.5" /> }

  return (
    <div className="space-y-3">
      {/* 개정묶음 일괄 승인 툴바 */}
      {canApprove && pendingCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-amber-800 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleSelectAll}
              className="w-4 h-4 rounded border-amber-400 text-orange-600 focus:ring-orange-500"
            />
            미승인 전체 선택 ({selectedRevIds.length}/{pendingCount})
          </label>
          <div className="flex items-center gap-2">
            {onRejectSelected && (
              <button
                onClick={onRejectSelected}
                disabled={selectedRevIds.length === 0 || rejecting}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-red-300 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed text-red-600 rounded-lg text-sm font-semibold"
              >
                <XCircle className="w-4 h-4" />
                {rejecting ? "반려 처리 중..." : "선택 항목 일괄 반려"}
              </button>
            )}
            <button
              onClick={onOpenApprove}
              disabled={selectedRevIds.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold"
            >
              <CheckCircle2 className="w-4 h-4" />
              선택 항목 일괄 승인 (개정 1회)
            </button>
          </div>
        </div>
      )}

      {revisions.map((rev) => {
        const meta = statusMeta(rev.approvalStatus)
        const typeLabel = rev.changeType === "create" ? "신규 추가" : rev.changeType === "delete" ? "삭제" : "수정"
        const isPending = rev.approvalStatus === "pending"
        const isChecked = selectedRevIds.includes(rev._id)
        const selectable = isPending && canApprove
        return (
          <div
            key={rev._id}
            onClick={selectable ? () => onToggleSelect(rev._id) : undefined}
            className={`bg-white rounded-xl border p-4 transition-colors ${
              selectable ? "cursor-pointer hover:border-orange-300" : ""
            } ${
              isPending && isChecked ? "border-orange-400 ring-1 ring-orange-200" : "border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex items-start gap-3">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggleSelect(rev._id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 w-4 h-4 shrink-0 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                  />
                )}
                <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`}>
                    {meta.icon}
                    {meta.label}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{typeLabel}</span>
                  <span className="font-semibold text-gray-900">
                    {rev.category} → {rev.subCategory}
                  </span>
                  {rev.version && rev.approvalStatus === "approved" && (
                    <span className="text-xs font-semibold text-orange-600">v{rev.version}</span>
                  )}
                </div>
                <div className="mt-1.5 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span>수정 요청자: <b className="text-gray-700">{rev.requestedByName || "-"}</b></span>
                  <span>웹 수정자: <b className="text-gray-700">{rev.editedByName}</b></span>
                  <span>{fmt(rev.createdAt)}</span>
                  {rev.approvalStatus !== "pending" && (
                    <span>처리: <b className="text-gray-700">{rev.approvedByName || "-"}</b> ({fmt(rev.approvedAt)})</span>
                  )}
                </div>
                {rev.changeReason && (
                  <p className="mt-2 text-sm text-gray-600">변경 사유: {rev.changeReason}</p>
                )}
                {rev.rejectReason && (
                  <p className="mt-1 text-sm text-red-600">반려 사유: {rev.rejectReason}</p>
                )}
                {rev.changesetName && rev.approvalStatus === "approved" && (
                  <p className="mt-1 text-xs text-gray-400">개정안: {rev.changesetName}</p>
                )}
                </div>
              </div>

              {rev.approvalStatus === "pending" && canApprove && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onReject(rev)
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    반려
                  </button>
                </div>
              )}
            </div>

            {/* before → after 대비 (수정인 경우) */}
            {rev.changeType === "update" && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1.5">변경 전</p>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">요구 사양</div>
                  <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: rev.requirementBefore || "-" }} />
                  <div className="text-xs font-medium text-gray-500 mt-2 mb-0.5">제작 가이드라인</div>
                  <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: rev.guidelineBefore || "-" }} />
                  <div className="text-xs font-medium text-gray-500 mt-2 mb-0.5">사용 부품</div>
                  <PartsList parts={parseParts(rev.partsBefore)} />
                </div>
                <div className="min-w-0 rounded-lg border border-green-200 bg-green-50 p-3">
                  <p className="text-xs font-semibold text-green-600 mb-1.5">변경 후</p>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">요구 사양</div>
                  <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: rev.requirementAfter || "-" }} />
                  <div className="text-xs font-medium text-gray-500 mt-2 mb-0.5">제작 가이드라인</div>
                  <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: rev.guidelineAfter || "-" }} />
                  <div className="text-xs font-medium text-gray-500 mt-2 mb-0.5">사용 부품</div>
                  <PartsList parts={parseParts(rev.partsAfter)} />
                </div>
              </div>
            )}

            {/* 신규 추가 시 부품 목록 */}
            {rev.changeType === "create" && parseParts(rev.partsAfter).length > 0 && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-xs font-semibold text-green-600 mb-1">사용 부품</p>
                <PartsList parts={parseParts(rev.partsAfter)} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ==================== 사양서 생성 모달 ====================
function CreateSpecModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (data: { specTitle: string; sourceCompany: string; docNumber: string }) => void
}) {
  const [specTitle, setSpecTitle] = useState("")
  const [sourceCompany, setSourceCompany] = useState("")
  const [docNumber, setDocNumber] = useState("")

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 mb-4">제작 사양서 생성</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사양서 제목 *</label>
            <input
              value={specTitle}
              onChange={(e) => setSpecTitle(e.target.value)}
              placeholder="예: 400A 트랙/캐리지 용접 자동화 개발 사양서"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">원청사</label>
            <input
              value={sourceCompany}
              onChange={(e) => setSourceCompany(e.target.value)}
              placeholder="예: 현대중공업"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">문서번호</label>
            <input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="예: PT-SPEC-25-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
            취소
          </button>
          <button
            onClick={() => {
              if (!specTitle.trim()) {
                toast.error("사양서 제목을 입력하세요.")
                return
              }
              onCreate({ specTitle: specTitle.trim(), sourceCompany: sourceCompany.trim(), docNumber: docNumber.trim() })
            }}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium"
          >
            생성
          </button>
        </div>
      </div>
    </div>
  )
}

// ==================== 개정묶음(Changeset) 일괄 승인 모달 ====================
function ApproveChangesetModal({
  count,
  nextVersion,
  approving,
  onClose,
  onConfirm,
}: {
  count: number
  nextVersion: number
  approving: boolean
  onClose: () => void
  onConfirm: (changesetName: string) => void
}) {
  const defaultName = `${new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit" })} 개정 (v${nextVersion})`
  const [name, setName] = useState(defaultName)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  // 개정안 이름이 "최초 작성"이면 총 수정 반영 횟수를 카운트하지 않음(최초 작성은 수정이 아니므로)
  const isInitialWrite = name.trim() === INITIAL_WRITE_NAME

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle2 className="w-5 h-5 text-green-600" />
        <h2 className="text-lg font-bold text-gray-900">개정묶음 일괄 승인</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        선택한 <b className="text-gray-800">{count}건</b>의 수정을 하나의 개정으로 묶어 반영합니다.
        사양서 버전은 <b className="text-orange-600">v{nextVersion}</b> 로 1만 증가합니다.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">개정안 이름</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 2024-07 3차 개정"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
        />
        <p className="mt-1 text-xs text-gray-400">이 이름으로 승인된 모든 항목이 함께 기록됩니다.</p>
        <button
          type="button"
          onClick={() => setName(INITIAL_WRITE_NAME)}
          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600"
        >
          "{INITIAL_WRITE_NAME}"으로 설정
        </button>
      </div>

      {/* 최초 작성 안내 코멘트 */}
      <div className={`mt-3 rounded-lg border px-3 py-2 text-xs leading-relaxed ${
        isInitialWrite ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-gray-50 border-gray-200 text-gray-500"
      }`}>
        <b>안내:</b> 개정안 이름을 <b>"{INITIAL_WRITE_NAME}"</b>으로 입력하면 <b>총 수정 반영 횟수에 포함되지 않습니다.</b>{" "}
        (최초 작성은 수정이 아니므로, 몇 번을 "{INITIAL_WRITE_NAME}"으로 승인해도 수정 반영 횟수는 계속 0입니다.)
        {isInitialWrite && <span className="block mt-1 font-semibold">✓ 현재 "{INITIAL_WRITE_NAME}"으로 설정되어 있어 수정 반영 횟수가 증가하지 않습니다.</span>}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={onClose}
          disabled={approving}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50"
        >
          취소
        </button>
        <button
          onClick={() => onConfirm(name.trim())}
          disabled={approving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-60"
        >
          <CheckCircle2 className="w-4 h-4" />
          {approving ? "승인 처리 중..." : "개정 승인"}
        </button>
      </div>
    </ModalShell>
  )
}

// ==================== 사양서 이름/메타 정보 수정 모달 ====================
function EditSpecModal({
  spec,
  saving,
  onClose,
  onSave,
}: {
  spec: EquipmentSpec
  saving: boolean
  onClose: () => void
  onSave: (data: { specTitle: string; sourceCompany: string; docNumber: string }) => void
}) {
  const [specTitle, setSpecTitle] = useState(spec.specTitle || "")
  const [sourceCompany, setSourceCompany] = useState(spec.sourceCompany || "")
  const [docNumber, setDocNumber] = useState(spec.docNumber || "")

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <Pencil className="w-5 h-5 text-orange-600" />
          <h2 className="text-lg font-bold text-gray-900">사양서 정보 수정</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사양서 제목 *</label>
            <input
              value={specTitle}
              onChange={(e) => setSpecTitle(e.target.value)}
              placeholder="예: 400A 트랙/캐리지 용접 자동화 개발 사양서"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">원청사</label>
            <input
              value={sourceCompany}
              onChange={(e) => setSourceCompany(e.target.value)}
              placeholder="예: 현대중공업"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">문서번호</label>
            <input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="예: PT-SPEC-25-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={() => onSave({ specTitle, sourceCompany, docNumber })}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-60"
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ==================== 공용 모달 셸 ====================
// 드래그로 텍스트를 선택하다가 마우스가 오버레이 밖/위에서 끝나도 창이 닫히지 않도록,
// "오버레이에서 mousedown이 시작된 경우"에만 닫는다. (단순 클릭 닫기는 유지)
function ModalShell({
  children,
  onClose,
  maxWidth = "max-w-md",
}: {
  children: React.ReactNode
  onClose: () => void
  maxWidth?: string
}) {
  const downOnOverlay = useRef(false)
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => {
        downOnOverlay.current = e.target === e.currentTarget
      }}
      onMouseUp={(e) => {
        if (downOnOverlay.current && e.target === e.currentTarget) onClose()
        downOnOverlay.current = false
      }}
    >
      <div
        className={`bg-white rounded-2xl w-full ${maxWidth} my-8 p-6`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

// ==================== 템플릿 저장 모달 ====================
function SaveTemplateModal({
  itemCount,
  saving,
  onClose,
  onSave,
}: {
  itemCount: number
  saving: boolean
  onClose: () => void
  onSave: (templateName: string, description: string) => void
}) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center gap-2 mb-1">
        <Save className="w-5 h-5 text-orange-600" />
        <h2 className="text-lg font-bold text-gray-900">템플릿으로 저장</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        현재 사양서의 <b className="text-gray-800">{itemCount}개 항목</b>을 템플릿으로 저장합니다. 다른 사양서에서 불러와 재사용할 수 있습니다.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">템플릿 이름 *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="예: 표준 용접 자동화 기본 사양"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="예: 개발장비 공통 항목 모음"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium disabled:opacity-50">
          취소
        </button>
        <button
          onClick={() => onSave(name, description)}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium disabled:opacity-60"
        >
          {saving ? "저장 중..." : "템플릿 저장"}
        </button>
      </div>
    </ModalShell>
  )
}

// ==================== 템플릿 불러오기 모달 ====================
function LoadTemplateModal({
  templates,
  importing,
  parseTemplateItems,
  onClose,
  onLoad,
  onDelete,
}: {
  templates: SpecTemplate[]
  importing: boolean
  parseTemplateItems: (tpl: SpecTemplate) => TemplateItem[]
  onClose: () => void
  onLoad: (tpl: SpecTemplate) => void
  onDelete: (tpl: SpecTemplate) => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-lg">
      <div className="flex items-center gap-2 mb-1">
        <FolderDown className="w-5 h-5 text-orange-600" />
        <h2 className="text-lg font-bold text-gray-900">템플릿 불러오기</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">선택한 템플릿의 사양 항목을 현재 사양서에 추가합니다.</p>

      {templates.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-300 rounded-xl">
          저장된 템플릿이 없습니다.
        </div>
      ) : (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {templates.map((tpl) => {
            const count = parseTemplateItems(tpl).length
            return (
              <div key={tpl._id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-3 hover:border-orange-300">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{tpl.templateName}</p>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="font-medium text-orange-600">{count}개 항목</span>
                    {tpl.description && <span>{tpl.description}</span>}
                    {tpl.creatorName && <span>{tpl.creatorName}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => onLoad(tpl)}
                    disabled={importing}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-semibold disabled:opacity-60"
                  >
                    <FolderDown className="w-3.5 h-3.5" />
                    불러오기
                  </button>
                  <button
                    onClick={() => onDelete(tpl)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                    title="템플릿 삭제"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
          닫기
        </button>
      </div>
    </ModalShell>
  )
}
